import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { LoadingScreen, ButtonSpinner } from '../components/ui/Spinner';
import { formatCurrency, formatDateTime } from '../utils/formatting';
import { ArrowLeftIcon, CheckIcon, PencilIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

const DECISION_QUALITY_CHOICES = [
    { value: 'correct', label: 'Correct', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
    { value: 'mistake', label: 'Mistake', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
    { value: 'neutral', label: 'Neutral', color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' },
    { value: 'pending', label: 'Pending Review', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' },
];

const SEVERITY_CHOICES = [
    { value: 'minor', label: 'Minor' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'severe', label: 'Severe' },
];

// Map tag colors to Tailwind classes
const TAG_COLOR_CLASSES = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300',
    pink: 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300',
    indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
    teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300',
    emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300',
    cyan: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
    violet: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300',
    gray: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
};

function HandDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [hand, setHand] = useState(null);
    const [leaks, setLeaks] = useState([]);
    const [availableTags, setAvailableTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [newTagInput, setNewTagInput] = useState('');

    // Form state
    const [decisionQuality, setDecisionQuality] = useState('pending');
    const [severity, setSeverity] = useState('');
    const [reviewAnalysis, setReviewAnalysis] = useState('');
    const [selectedLeaks, setSelectedLeaks] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [reviewed, setReviewed] = useState(false);
    const [outcome, setOutcome] = useState('');
    const [amount, setAmount] = useState('');

    useEffect(() => {
        fetchHand();
        fetchLeaks();
        fetchTags();
    }, [id]);

    const fetchHand = async () => {
        try {
            const response = await axios.get(`/api/hands/${id}/`);
            const data = response.data;
            setHand(data);
            setDecisionQuality(data.decision_quality || 'pending');
            setSeverity(data.severity || '');
            setReviewAnalysis(data.review_analysis || '');
            setSelectedLeaks(data.leaks?.map(l => l.id) || []);
            setSelectedTags(data.tags || []);
            setReviewed(data.reviewed || false);
            setOutcome(data.outcome || '');
            setAmount(data.amount ? Math.abs(parseFloat(data.amount)).toString() : '');
        } catch (err) {
            console.error('Failed to fetch hand:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeaks = async () => {
        try {
            const response = await axios.get('/api/leaks/');
            setLeaks(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch leaks:', err);
        }
    };

    const fetchTags = async () => {
        try {
            const response = await axios.get('/api/hand-tags/');
            setAvailableTags(response.data?.results || response.data || []);
        } catch (err) {
            console.error('Failed to fetch tags:', err);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            // Calculate amount with sign based on outcome
            let amountValue = null;
            if (amount && outcome) {
                const absAmount = Math.abs(parseFloat(amount));
                amountValue = outcome === 'lost' ? -absAmount : absAmount;
            }

            await axios.patch(`/api/hands/${id}/`, {
                decision_quality: decisionQuality,
                severity: decisionQuality === 'mistake' ? severity : null,
                review_analysis: reviewAnalysis,
                leaks_ids: selectedLeaks,
                tags: selectedTags,
                reviewed: true,
                outcome: outcome || null,
                amount: amountValue,
            });
            setReviewed(true);
            setIsEditing(false);
            fetchHand();
        } catch (err) {
            console.error('Failed to save:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleAddTag = async () => {
        const tagName = newTagInput.trim().toLowerCase();
        if (!tagName || selectedTags.includes(tagName)) {
            setNewTagInput('');
            return;
        }

        // Add to selected tags
        setSelectedTags([...selectedTags, tagName]);
        setNewTagInput('');

        // Create tag in backend if it doesn't exist
        try {
            await axios.post('/api/hand-tags/get_or_create/', { name: tagName });
            fetchTags(); // Refresh available tags
        } catch (err) {
            console.error('Failed to create tag:', err);
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setSelectedTags(selectedTags.filter(t => t !== tagToRemove));
    };

    const handleToggleTag = (tagName) => {
        if (selectedTags.includes(tagName)) {
            setSelectedTags(selectedTags.filter(t => t !== tagName));
        } else {
            setSelectedTags([...selectedTags, tagName]);
        }
    };

    const getTagColorClass = (tagName) => {
        const tag = availableTags.find(t => t.name === tagName);
        return TAG_COLOR_CLASSES[tag?.color] || TAG_COLOR_CLASSES.gray;
    };

    const getDecisionQualityColor = (value) => {
        const choice = DECISION_QUALITY_CHOICES.find(c => c.value === value);
        return choice?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    };

    if (loading) return <LoadingScreen />;
    if (!hand) return <div className="p-4 text-red-500">Hand not found</div>;

    return (
        <div className="min-h-screen bg-[#faf8f5] dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                            <ArrowLeftIcon className="w-5 h-5" />
                            <span>Back</span>
                        </button>
                        <div className="text-right">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                Hand #{hand.id}
                            </p>
                            <p className="text-xs text-gray-500">
                                {hand.session_location} - {formatDateTime(hand.session_date)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 py-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                    {/* Header */}
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                                {hand.decision_quality && hand.decision_quality !== 'pending' && (
                                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${getDecisionQualityColor(hand.decision_quality)}`}>
                                        {hand.decision_quality_display}
                                    </span>
                                )}
                                {hand.severity && (
                                    <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                                        {hand.severity_display}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {!isEditing && (
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                                    >
                                        <PencilIcon className="w-4 h-4" />
                                        Edit
                                    </button>
                                )}
                                {reviewed && (
                                    <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                                        Reviewed
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Tags */}
                        {hand.tags && hand.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-2">
                                {hand.tags.map(tag => (
                                    <span key={tag} className={`text-xs px-2 py-0.5 rounded ${getTagColorClass(tag)}`}>
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Linked leaks */}
                        {hand.leaks && hand.leaks.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {hand.leaks.map(leak => (
                                    <span key={leak.id} className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded">
                                        {leak.name}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Hand Description */}
                    <div className="p-4 space-y-4">
                        <div>
                            <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                                {hand.hand_description}
                            </p>
                        </div>

                        {/* Outcome/Amount */}
                        {hand.outcome && hand.outcome !== 'unknown' && (
                            <div className="flex items-center gap-4">
                                <span className={`text-sm font-medium ${hand.outcome === 'won' ? 'text-green-600' : hand.outcome === 'lost' ? 'text-red-600' : 'text-gray-600'}`}>
                                    {hand.outcome_display}: {hand.amount ? formatCurrency(Math.abs(parseFloat(hand.amount))) : 'N/A'}
                                </span>
                            </div>
                        )}

                        {/* Notes */}
                        {hand.notes && (
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-3">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">In-session notes:</p>
                                <p className="text-sm text-gray-700 dark:text-gray-300">{hand.notes}</p>
                            </div>
                        )}

                        {/* Session link */}
                        <div className="pt-2">
                            <Link
                                to={`/sessions/${hand.session}`}
                                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                            >
                                View full session →
                            </Link>
                        </div>
                    </div>

                    {/* Review Section */}
                    {isEditing && (
                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Review & Edit
                                </div>
                                <button
                                    onClick={() => setIsEditing(false)}
                                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                                >
                                    <XMarkIcon className="w-4 h-4" />
                                    Cancel
                                </button>
                            </div>

                            {/* Tags */}
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    Tags
                                </label>
                                {/* Selected tags */}
                                <div className="flex flex-wrap gap-1 mb-2">
                                    {selectedTags.map(tag => (
                                        <span
                                            key={tag}
                                            className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${getTagColorClass(tag)}`}
                                        >
                                            {tag}
                                            <button
                                                onClick={() => handleRemoveTag(tag)}
                                                className="hover:opacity-70"
                                            >
                                                <XMarkIcon className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}
                                </div>
                                {/* Add new tag */}
                                <div className="flex gap-2 mb-2">
                                    <input
                                        type="text"
                                        value={newTagInput}
                                        onChange={(e) => setNewTagInput(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                                        className="flex-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                        placeholder="Add new tag..."
                                    />
                                    <button
                                        onClick={handleAddTag}
                                        className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                    </button>
                                </div>
                                {/* Available tags */}
                                <div className="flex flex-wrap gap-1">
                                    {availableTags
                                        .filter(t => !selectedTags.includes(t.name))
                                        .slice(0, 15)
                                        .map(tag => (
                                            <button
                                                key={tag.id}
                                                onClick={() => handleToggleTag(tag.name)}
                                                className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                                            >
                                                + {tag.name}
                                            </button>
                                        ))}
                                </div>
                            </div>

                            {/* Decision Quality */}
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    Decision Quality
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {DECISION_QUALITY_CHOICES.map(choice => (
                                        <button
                                            key={choice.value}
                                            onClick={() => setDecisionQuality(choice.value)}
                                            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                                decisionQuality === choice.value
                                                    ? choice.color + ' ring-2 ring-offset-2 ring-current'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            {choice.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Severity */}
                            {decisionQuality === 'mistake' && (
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
                                        Severity
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        {SEVERITY_CHOICES.map(choice => (
                                            <button
                                                key={choice.value}
                                                onClick={() => setSeverity(choice.value)}
                                                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                                    severity === choice.value
                                                        ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-2 ring-offset-2 ring-red-500'
                                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                }`}
                                            >
                                                {choice.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Outcome & Amount */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
                                        Outcome
                                    </label>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setOutcome('won')}
                                            className={`flex-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                                outcome === 'won'
                                                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-2 ring-offset-2 ring-green-500'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            Won
                                        </button>
                                        <button
                                            onClick={() => setOutcome('lost')}
                                            className={`flex-1 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                                outcome === 'lost'
                                                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-2 ring-offset-2 ring-red-500'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            Lost
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
                                        Amount
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                                        <input
                                            type="number"
                                            value={amount}
                                            onChange={(e) => setAmount(e.target.value)}
                                            className="w-full pl-7 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                            placeholder="0"
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Analysis */}
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                    What went wrong/right? How to improve?
                                </label>
                                <textarea
                                    value={reviewAnalysis}
                                    onChange={(e) => setReviewAnalysis(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                    rows={3}
                                    placeholder="Analyze this hand..."
                                />
                            </div>

                            {/* Leaks */}
                            <div>
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
                                    Link to Leaks
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {leaks.map(leak => (
                                        <label
                                            key={leak.id}
                                            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs cursor-pointer transition-colors ${
                                                selectedLeaks.includes(leak.id)
                                                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-transparent hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedLeaks.includes(leak.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedLeaks([...selectedLeaks, leak.id]);
                                                    } else {
                                                        setSelectedLeaks(selectedLeaks.filter(id => id !== leak.id));
                                                    }
                                                }}
                                                className="sr-only"
                                            />
                                            {leak.name}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {/* Save Button */}
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {saving ? <ButtonSpinner /> : <CheckIcon className="w-4 h-4" />}
                                Save & Mark Reviewed
                            </button>
                        </div>
                    )}

                    {/* Show existing review analysis if not editing */}
                    {!isEditing && hand.review_analysis && (
                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Review Analysis:</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                {hand.review_analysis}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default HandDetailPage;
